import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ProfileSwitcher } from '../ProfileSwitcher';
import {
    LayoutDashboard,
    Users,
    Dumbbell,
    Library,
    DollarSign,
    MessageSquare,
    PlayCircle,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Zap,
    TrendingUp as TrendingIcon,
    Calendar as CalendarIcon,
    Watch,
} from 'lucide-react';
import { useState } from 'react';
import { LucideIcon } from 'lucide-react';

interface NavigationItem {
    name: string;
    href: string;
    icon: LucideIcon;
    premium?: boolean;
}

const navigationMap: Record<string, NavigationItem[]> = {
    personal: [
        { name: 'Painel', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Meus Alunos', href: '/atletas', icon: Users },
        { name: 'Montador de Treinos', href: '/workouts', icon: Dumbbell },
        { name: 'Biblioteca de Exercícios', href: '/exercises', icon: Library },
        { name: 'Financeiro', href: '/financial', icon: DollarSign, premium: true },
        { name: 'WhatsApp', href: '/whatsapp', icon: MessageSquare, premium: true },
        { name: 'Wearables', href: '/wearables', icon: Watch, premium: true },
        { name: 'Conteúdo Educativo', href: '/content', icon: PlayCircle },
        { name: 'Minha Bio', href: '/bio', icon: Zap, premium: true },
        { name: 'Configurações', href: '/settings', icon: Settings },
    ],
    student: [
        { name: 'Meu Painel', href: '/aluno', icon: LayoutDashboard },
        { name: 'Treino de Hoje', href: '/aluno', icon: Dumbbell },
        { name: 'Minha Evolução', href: '/aluno/progresso', icon: TrendingIcon },
        { name: 'Check-in Semanal', href: '/aluno/check-in', icon: CalendarIcon },
        { name: 'Encontrar Personal', href: '/aluno/buscar-personal', icon: Users },
        { name: 'Conteúdos', href: '/aluno/conteudo', icon: PlayCircle },
        { name: 'Meu Perfil', href: '/aluno/perfil', icon: Settings },
    ],
    influencer: [
        { name: 'Dashboard', href: '/influencer', icon: LayoutDashboard },
        { name: 'Configurações', href: '/settings', icon: Settings },
    ],
};

export function AppLayout() {
    const { profile, tenant, signOut } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                <div className="flex h-full flex-col">
                    {/* Logo */}
                    <div className="flex h-16 items-center justify-between px-4 border-b border-gray-100 dark:border-gray-800">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/gofit-logo.png" alt="GoFit" className="w-9 h-9" />
                            <span className="font-bold text-lg text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                GoFit
                            </span>
                        </Link>
                        <button
                            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tenant info */}
                    {tenant && (
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {tenant.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                /{tenant.slug}
                            </p>
                        </div>
                    )}

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-4 overflow-y-auto">
                        <ul className="space-y-1">
                            {(navigationMap[profile?.role as keyof typeof navigationMap] || []).map((item) => {
                                const isActive = location.pathname === item.href;
                                const Icon = item.icon;

                                return (
                                    <li key={item.name}>
                                        <Link
                                            to={item.href}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300'
                                                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                                                }`}
                                            onClick={() => setSidebarOpen(false)}
                                        >
                                            <Icon className="w-5 h-5" />
                                            <span>{item.name}</span>
                                            {item.premium && (
                                                <span className="ml-auto badge-primary text-[10px]">PRO</span>
                                            )}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    {/* User section */}
                    <div className="p-3 border-t border-gray-200 dark:border-gray-800">
                        <div className="relative">
                            <button
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                                className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                                    <span className="text-primary-700 dark:text-primary-300 font-medium">
                                        {profile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                                    </span>
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {profile?.full_name || 'Usuário'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {profile?.email}
                                    </p>
                                </div>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>

                            {userMenuOpen && (
                                <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 space-y-1">
                                    {/* Profile Switcher */}
                                    <div className="px-2 py-1">
                                        <ProfileSwitcher />
                                    </div>
                                    <hr className="border-gray-200 dark:border-gray-700" />
                                    <button
                                        onClick={handleSignOut}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        <span>Sair</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Top header (mobile) */}
                <header className="sticky top-0 z-30 flex h-16 items-center gap-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-4 lg:hidden">
                    <button
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <img src="/gofit-logo.png" alt="GoFit" className="w-8 h-8" />
                        <span className="font-bold text-lg text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            GoFit
                        </span>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 lg:p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
